import { useState, useEffect, useCallback, useMemo } from 'react';
import { rpc, RpcError } from '@/api/client';
import { useRouter } from '@/context/RouterContext';
import { useI18n } from '@/context/I18nContext';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorState } from '@/components/common/ErrorState';
import { ArrowLeft, Trophy, Timer } from 'lucide-react';
import type { GetRaceEventResponse } from '@/types/telemetry';
import { formatLapTime } from './ResultsTable';

interface EventOverviewProps {
  championshipId: string;
  eventId: string;
}

export function EventOverview({ championshipId, eventId }: EventOverviewProps) {
  const { navigate } = useRouter();
  const { t } = useI18n();

  const [eventData, setEventData] = useState<GetRaceEventResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const CATEGORY_ORDER = ['Micro', 'Mini', 'Junior', 'Club', 'Max', 'Master', 'DD2', 'DD2 Master'];

  const loadEvent = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await rpc<GetRaceEventResponse>('GetRaceEvent', { event_id: eventId });
      setEventData(res);
    } catch (e: any) {
      const nf = e instanceof RpcError && e.isNotFound;
      setNotFound(nf);
      setError(nf ? 'The requested race event could not be found.' : (e.message || t('races.failedToLoad')));
    } finally {
      setLoading(false);
    }
  }, [eventId, t]);

  useEffect(() => { loadEvent(); }, [loadEvent]);

  // Derived: sorted categories based on categorySummaries
  const categories = useMemo(() => {
    if (!eventData?.categorySummaries) return [];
    return [...eventData.categorySummaries].sort((a, b) => {
      const ai = CATEGORY_ORDER.indexOf(a.categoryName);
      const bi = CATEGORY_ORDER.indexOf(b.categoryName);
      if (ai >= 0 && bi >= 0) return ai - bi;
      if (ai >= 0) return -1;
      if (bi >= 0) return 1;
      return a.categoryName.localeCompare(b.categoryName);
    });
  }, [eventData]);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorState message={error} isNotFound={notFound} />;
  if (!eventData) return <ErrorState message={t('races.noData')} />;

  const { event } = eventData;

  return (
    <div>
      {/* Back */}
      <button
        onClick={() => navigate(`#/races/${championshipId}`)}
        className="flex items-center gap-1.5 text-xs text-text-muted hover:text-text-primary transition-colors mb-4 cursor-pointer"
      >
        <ArrowLeft size={14} />
        {t('common.back')}
      </button>

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold tracking-tight">{event.trackName}</h1>
        <div className="flex items-center gap-3 mt-2 text-sm text-text-muted">
          <span>{t('races.round', { n: event.roundNumber })}</span>
          <span>&middot;</span>
          <span>{formatDate(event.dateStart)}</span>
          <span>&middot;</span>
          <span>{event.country}</span>
        </div>
      </div>

      {/* Categories Table */}
      <div className="card overflow-hidden mt-4">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[500px] border-collapse">
            <thead>
              <tr className="divider">
                <th className="text-left py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('races.category')}</th>
                <th className="text-left py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('races.pole')}</th>
                <th className="text-left py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('races.winner')}</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((cat, index) => (
                <tr
                  key={cat.categoryId}
                  onClick={() => navigate(`#/races/${championshipId}/${eventId}/${encodeURIComponent(cat.categoryName)}`)}
                  className={`hover:bg-bg-hover transition-colors cursor-pointer group ${index < categories.length - 1 ? 'divider' : ''}`}
                >
                  <td className="py-3 px-4">
                    <span className="font-semibold text-text-primary group-hover:text-accent transition-colors">
                      {cat.categoryName}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-text-secondary">
                    {cat.fastestQualiName ? (
                      <div className="flex items-center gap-1.5">
                        <Timer size={13} className="text-accent" />
                        <span>{cat.fastestQualiName} (<span className="mono">{formatLapTime(cat.fastestQualiLapMs || 0)}</span>)</span>
                      </div>
                    ) : (
                      <span className="text-text-muted">—</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-text-secondary">
                    {cat.winnerName ? (
                      <div className="flex items-center gap-1.5">
                        <Trophy size={13} className="text-yellow" />
                        <span>{cat.winnerName}</span>
                      </div>
                    ) : (
                      <span className="text-text-muted">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {categories.length === 0 && (
        <p className="text-sm text-text-muted text-center py-12">{t('races.noData')}</p>
      )}
    </div>
  );
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '–';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return dateStr;
  }
}
